import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Role, User } from "../../../../../api";
import { useAuth } from "../../../../../hooks/useAuth";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { ACTIONS, ENV } from "../../../../../utils/constants";

import {
  PASSWORD_REQUIREMENTS_MESSAGE,
  getUserPayload,
  initialValues,
  isStrongPassword,
} from "./CreateUserPage.form";

import styles from "./CreateUserPage.module.scss";

function getUserResponse(result) {
  return result?.response || result?.user || result || {};
}

function mapUserToForm(user = {}) {
  return {
    ...initialValues,
    firstname: user.firstname || "",
    lastname: user.lastname || "",
    email: user.email || "",
    password: "",
    number: user.number ?? "",
    role: user.role || "CUSTOMER",
    roles: Array.isArray(user.roles)
      ? user.roles.map((role) => (typeof role === "object" ? role._id : role))
      : [],
    active: user.active !== false,
    avatar: null,
  };
}

function getAvatarUrl(avatarPath) {
  if (!avatarPath) {
    return "";
  }

  if (avatarPath.startsWith("http://") || avatarPath.startsWith("https://")) {
    return avatarPath;
  }

  const normalizedPath = avatarPath.replace(/\\/g, "/").replace(/^\/+/, "");

  return `${ENV.SERVER_HOST}/${normalizedPath}`;
}

export function CreateUserPage() {
  const { userId } = useParams();
  const isEditMode = Boolean(userId);

  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { hasAction } = usePermissions();

  // Activar/desactivar la cuenta y asignarle roles son permisos propios,
  // separados de "editar" (MOD) -- no todo el que puede corregir el
  // nombre o el correo de un usuario debería poder también suspender su
  // acceso o cambiarle los roles (eso decide qué puede hacer en todo el
  // panel).
  const canActivate = hasAction("users", ACTIONS.ACTIVATE);
  const canAssignRoles = hasAction("users", ACTIONS.ASSIGN_ROLES);

  const [formValues, setFormValues] = useState(initialValues);
  const [existingAvatarPath, setExistingAvatarPath] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("");

  const [availableRoles, setAvailableRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);

  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [validationErrors, setValidationErrors] = useState({});
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    async function loadUser() {
      try {
        setIsLoading(true);
        setServerError("");

        const userCtrl = new User();
        const result = await userCtrl.getUser(userId);
        const user = getUserResponse(result);

        if (!user?._id) {
          throw new Error("No se encontró el usuario solicitado.");
        }

        setFormValues(mapUserToForm(user));
        setExistingAvatarPath(user.avatar || "");
      } catch (errorResponse) {
        console.error("loadUser error:", errorResponse);

        setServerError(
          errorResponse?.msg ||
            "No fue posible cargar el usuario solicitado."
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadUser();
  }, [isEditMode, userId]);

  useEffect(() => {
    async function loadRoles() {
      try {
        setIsLoadingRoles(true);

        const roleCtrl = new Role();
        const result = await roleCtrl.getRoles(1, 100);
        const response = result?.roles || result?.response || result || {};

        const docs = Array.isArray(response.docs)
          ? response.docs
          : Array.isArray(response)
            ? response
            : [];

        setAvailableRoles(docs);
      } catch (errorResponse) {
        console.error("loadRoles error:", errorResponse);
        setAvailableRoles([]);
      } finally {
        setIsLoadingRoles(false);
      }
    }

    loadRoles();
  }, []);

  useEffect(() => {
    if (!formValues.avatar) {
      return undefined;
    }

    const objectUrl = URL.createObjectURL(formValues.avatar);
    setAvatarPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [formValues.avatar]);

  const hasValidationErrors = Object.values(validationErrors).some(Boolean);

  const avatarUrl = useMemo(
    () => avatarPreview || getAvatarUrl(existingAvatarPath),
    [avatarPreview, existingAvatarPath]
  );

  const isEditingSelf =
    isEditMode && String(userId) === String(currentUser?._id);

  function clearFieldError(fieldName) {
    setValidationErrors((currentErrors) => ({
      ...currentErrors,
      [fieldName]: "",
    }));
  }

  function handleToggleRole(roleId) {
    setFormValues((currentValues) => {
      const isChecked = currentValues.roles.includes(roleId);

      return {
        ...currentValues,
        roles: isChecked
          ? currentValues.roles.filter((id) => id !== roleId)
          : [...currentValues.roles, roleId],
      };
    });
  }

  function handleFieldChange(fieldName, value) {
    setFormValues((currentValues) => ({
      ...currentValues,
      [fieldName]: value,
    }));

    clearFieldError(fieldName);
  }

  function handleAvatarChange(event) {
    const file = event.target.files?.[0] || null;

    handleFieldChange("avatar", file);
  }

  function handleCancel() {
    if (!isSubmitting) {
      navigate("/admin/users");
    }
  }

  function validateForm() {
    const errors = {};

    if (!formValues.firstname.trim()) {
      errors.firstname = "El nombre es obligatorio.";
    }

    if (!formValues.lastname.trim()) {
      errors.lastname = "El apellido es obligatorio.";
    }

    if (!formValues.email.trim()) {
      errors.email = "El correo electrónico es obligatorio.";
    } else if (!/^\S+@\S+\.\S+$/.test(formValues.email.trim())) {
      errors.email = "El correo electrónico no es válido.";
    }

    if (!isEditMode && !formValues.password) {
      errors.password = "La contraseña es obligatoria.";
    } else if (formValues.password && !isStrongPassword(formValues.password)) {
      errors.password = PASSWORD_REQUIREMENTS_MESSAGE;
    }

    return errors;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setServerError("");
      return;
    }

    try {
      setIsSubmitting(true);
      setServerError("");

      const userCtrl = new User();
      const payload = getUserPayload(formValues);

      if (isEditMode) {
        await userCtrl.updateUser(userId, payload);
      } else {
        await userCtrl.createUser(payload);
      }

      navigate("/admin/users", {
        state: {
          success: isEditMode
            ? "Usuario actualizado correctamente."
            : "Usuario creado correctamente.",
        },
      });
    } catch (errorResponse) {
      console.error("handleSubmit error:", errorResponse);

      if (errorResponse?.errors) {
        setValidationErrors(errorResponse.errors);
      }

      setServerError(
        errorResponse?.msg ||
          "No fue posible guardar el usuario. Intenta nuevamente."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <section className={styles.CreateUserPage}>
        <div className={styles.StateMessage}>Cargando usuario...</div>
      </section>
    );
  }

  return (
    <section className={styles.CreateUserPage}>
      <header className={styles.PageHeader}>
        <div className={styles.TitleSection}>
          <button
            type="button"
            className={styles.BackButton}
            onClick={handleCancel}
            disabled={isSubmitting}
            aria-label="Volver a usuarios"
          >
            ←
          </button>

          <div>
            <p className={styles.Breadcrumb}>
              Usuarios / {isEditMode ? "Editar usuario" : "Nuevo usuario"}
            </p>

            <h1>{isEditMode ? "Editar usuario" : "Nuevo usuario"}</h1>

            <p className={styles.Subtitle}>
              {isEditMode
                ? "Actualiza los datos de acceso y los roles asignados."
                : "Crea una nueva cuenta de acceso al panel."}
            </p>
          </div>
        </div>

        <div className={styles.HeaderActions}>
          <button
            type="button"
            className={styles.CancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </button>

          <button
            type="submit"
            form="create-user-form"
            className={styles.PublishButton}
            disabled={isSubmitting || hasValidationErrors}
          >
            {isSubmitting
              ? "Guardando..."
              : isEditMode
                ? "Guardar cambios"
                : "Crear usuario"}
          </button>
        </div>
      </header>

      {serverError && (
        <div className={styles.ServerErrorMessage} role="alert">
          <span className={styles.MessageIcon} aria-hidden="true">
            !
          </span>

          <span>{serverError}</span>
        </div>
      )}

      <form
        id="create-user-form"
        className={styles.Form}
        onSubmit={handleSubmit}
        noValidate
      >
        <div className={styles.AvatarSection}>
          <div className={styles.AvatarPreview}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="" />
            ) : (
              <span>
                {(formValues.firstname || "?").charAt(0).toUpperCase()}
              </span>
            )}
          </div>

          <label className={styles.AvatarUploadButton}>
            Cambiar foto
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              hidden
            />
          </label>
        </div>

        <div className={styles.FormGrid}>
          <label className={styles.Field}>
            <span>Nombre</span>

            <input
              type="text"
              value={formValues.firstname}
              onChange={(event) =>
                handleFieldChange("firstname", event.target.value)
              }
              aria-invalid={Boolean(validationErrors.firstname)}
            />

            {validationErrors.firstname && (
              <span className={styles.FieldError}>
                {validationErrors.firstname}
              </span>
            )}
          </label>

          <label className={styles.Field}>
            <span>Apellido</span>

            <input
              type="text"
              value={formValues.lastname}
              onChange={(event) =>
                handleFieldChange("lastname", event.target.value)
              }
              aria-invalid={Boolean(validationErrors.lastname)}
            />

            {validationErrors.lastname && (
              <span className={styles.FieldError}>
                {validationErrors.lastname}
              </span>
            )}
          </label>

          <label className={styles.Field}>
            <span>Correo electrónico</span>

            <input
              type="email"
              value={formValues.email}
              onChange={(event) =>
                handleFieldChange("email", event.target.value)
              }
              aria-invalid={Boolean(validationErrors.email)}
            />

            {validationErrors.email && (
              <span className={styles.FieldError}>
                {validationErrors.email}
              </span>
            )}
          </label>

          <label className={styles.Field}>
            <span>Teléfono</span>

            <input
              type="tel"
              value={formValues.number}
              onChange={(event) =>
                handleFieldChange("number", event.target.value)
              }
            />
          </label>

          <label className={styles.Field}>
            <span>
              {isEditMode
                ? "Nueva contraseña (opcional)"
                : "Contraseña"}
            </span>

            <input
              type="password"
              value={formValues.password}
              onChange={(event) =>
                handleFieldChange("password", event.target.value)
              }
              autoComplete="new-password"
              aria-invalid={Boolean(validationErrors.password)}
            />

            {validationErrors.password ? (
              <span className={styles.FieldError}>
                {validationErrors.password}
              </span>
            ) : (
              <span className={styles.FieldHint}>
                {PASSWORD_REQUIREMENTS_MESSAGE}
              </span>
            )}
          </label>

          <label className={styles.CheckboxField}>
            <input
              type="checkbox"
              checked={formValues.active}
              disabled={isEditingSelf || !canActivate}
              onChange={(event) =>
                handleFieldChange("active", event.target.checked)
              }
            />

            <span>Usuario activo</span>
          </label>

          {!canActivate && (
            <p className={styles.FieldHint}>
              No tienes permiso para activar o desactivar usuarios.
            </p>
          )}
        </div>

        <div className={styles.RolesSection}>
          <span className={styles.RolesSectionLabel}>Roles</span>

          {!canAssignRoles ? (
            <p className={styles.RolesSectionHint}>
              No tienes permiso para asignar roles a los usuarios.
            </p>
          ) : isLoadingRoles ? (
            <p className={styles.RolesSectionHint}>Cargando roles...</p>
          ) : availableRoles.length === 0 ? (
            <p className={styles.RolesSectionHint}>
              Todavía no has creado roles. Puedes crearlos desde el módulo de
              Roles y asignárselos a este usuario después.
            </p>
          ) : (
            <div className={styles.RolesList}>
              {availableRoles.map((role) => (
                <label key={role._id} className={styles.RoleOption}>
                  <input
                    type="checkbox"
                    checked={formValues.roles.includes(role._id)}
                    disabled={!canAssignRoles}
                    onChange={() => handleToggleRole(role._id)}
                  />

                  <span>{role.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

      </form>
    </section>
  );
}
